"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.ensureDataIsSerializable = ensureDataIsSerializable;
exports.hasOwnProperty = hasOwnProperty;
exports.isDataSerializable = isDataSerializable;
exports.iterateObject = iterateObject;
exports.removePropertiesWithName = removePropertiesWithName;
var _lodash = require("@avantia/lodash");
var _HpError = require("./HpError");
var _ignoreConstructorList = require("./ignoreConstructorList");
var _types = require("./types");
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
/*
Matches against error message from JSON.serialize(...):
  Converting circular structure to JSON
    --> starting at object with constructor 'Object'
        property 'two' -> object with constructor 'Object'
    --- property 'blah' closes the circle
*/
const circularExceptionRegex = / property '([^']+)' closes the circle/;
function hasOwnProperty(object, property) {
  return object !== undefined && object !== null ? Object.prototype.hasOwnProperty.call(object, property) : false;
}
function removePropertiesWithName({
  value,
  propName,
  maxDepth
}) {
  const action = (parent, _, key) => {
    if (key === propName) {
      delete parent[key];
      return _types.IterateObjectResponseTypes.Stop;
    }
    return _types.IterateObjectResponseTypes.Continue;
  };
  iterateObject(value, maxDepth || 10, action);
}
function ensureDataIsSerializable(data, options) {
  const {
    resultAsJson = false,
    formatted = false,
    maxCicularRefCheckDepth = 10
  } = options || {};
  return ensureDataIsSerializableInner(data, {
    resultAsJson,
    formatted,
    maxCicularRefCheckDepth
  }, 0);
}
function isDataSerializable(data, formatted) {
  try {
    const json = JSON.stringify(data, null, formatted ? 2 : undefined); // check to see if it can be serialized.
    return {
      result: json
    };
  } catch (ex) {
    return {
      error: new _HpError.HpError(ex)
    };
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
function iterateObject(item, maxDepth, action) {
  iterateObjectInner(item, null, '', action, 0, maxDepth);
}
function iterateObjectInner(item, parent, key, action, depth, maxDepth) {
  if (depth > maxDepth || (depth > 0 && action(parent, item, key)) === _types.IterateObjectResponseTypes.Stop) {
    return;
  }
  if (_lodash.lodash.isArray(item)) {
    item.forEach((v, ix) => iterateObjectInner(v, item, ix, action, depth + 1, maxDepth));
  } else if (typeof item === 'object') {
    _lodash.lodash.keys(item).forEach(childKey => {
      iterateObjectInner(item[childKey], item, childKey, action, depth + 1, maxDepth);
    });
  }
}
function ensureDataIsSerializableInner(data, options, depth) {
  if (!data || typeof data !== 'object') {
    return {
      result: data
    };
  }
  const {
    resultAsJson,
    formatted,
    maxCicularRefCheckDepth = 10
  } = options || {};
  if ((0, _ignoreConstructorList.hasUnserializableConstructors)()) {
    if (customPropertyProcessor(undefined, data, '') === _types.IterateObjectResponseTypes.Stop) {
      const error = new _HpError.HpDataSerializationError(`The data you are trying to log has been excluded from logging (due to complexity and/or circular references).`);
      error.info = 'To change the items that are excluded, use setIgnoreLogPropertiesWithConstructors(...)';
      error.invalidType = data.constructor.toString();
      return {
        error
      };
    }
    iterateObject(data, 2, customPropertyProcessor);
  }
  const {
    result: json,
    error
  } = isDataSerializable(data, formatted);
  if (json) {
    return {
      result: resultAsJson ? json : data
    };
  }
  const {
    name,
    message: exceptionMessage
  } = error;
  if (name === 'HpTypeError') {
    if (exceptionMessage && exceptionMessage.startsWith('Converting circular structure to JSON')) {
      const match = circularExceptionRegex.exec(exceptionMessage);
      if (match) {
        const propName = match[1];
        const propKey = 'removedPropertiesDueToCircularReferences';
        const newData = _objectSpread({}, data);
        const array = newData[propKey];
        if (array && array.indexOf(propName) < 0) {
          array.push(propName);
        } else {
          newData[propKey] = [propName];
        }
        removePropertiesWithName({
          value: newData,
          propName,
          maxDepth: maxCicularRefCheckDepth
        });
        const {
          result: json
        } = isDataSerializable(newData, formatted);
        if (json) {
          return {
            result: resultAsJson ? json : newData
          };
        } else if (depth < maxCicularRefCheckDepth) {
          return ensureDataIsSerializableInner(newData, options, depth + 1);
        }
      }
    }
  }

  // A catch-all, just in case, that shouldn't be needed.
  const message = (data ? data.message : null) || `The provided data could not be serialized.`;
  const err = new _HpError.HpDataSerializationError(error);
  err.addMessage(message);
  return {
    error: err
  };
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
function customPropertyProcessor(parent, item, key) {
  const constructor = item ? item.constructor : undefined;
  if (constructor && constructor !== Object && (0, _ignoreConstructorList.isUnserializableConstructor)(item.constructor)) {
    if (parent) {
      delete parent[key];
    }
    return _types.IterateObjectResponseTypes.Stop;
  }
  return _types.IterateObjectResponseTypes.Continue;
}